import { createSlice } from "@reduxjs/toolkit";
import {
  B314_DESIGN_FACTOR,
  b318DesignFactor,
  isPipelineCode,
  jointFactorValue,
  pipelineWall,
  temperatureDerating,
} from "../../utils/pipelineWall";

// Helper function: allowable stress when ASME B31.3 value isn’t provided
function computeAllowable(yieldStrength, tensileStrength) {
  const twoThirdsYield = (2 / 3) * yieldStrength;
  const oneThirdTensile = (1 / 3) * tensileStrength;
  return Math.min(twoThirdsYield, oneThirdTensile);
}

// Minimum walls on the allowable-stress basis (B31.3 / Section VIII style)
function computeAllowableThicknesses(state) {
  const {
    outsideDiameter,
    allowableStress,
    designPressure,
    testPressure,
    yieldStrength,
  } = state;

  if (!outsideDiameter || !allowableStress || !designPressure) {
    state.minWallWorking = null;
    state.minWallTest = null;
    state.minWallB313 = null;
    return;
  }

  const R0 = outsideDiameter / 2;
  const S = allowableStress;
  const P = designPressure;
  const Pt = testPressure || designPressure * 1.5;

  // Working-pressure thickness (same as before)
  state.minWallWorking = (P * R0) / (S + 0.5 * P);

  // Test-pressure thickness: limit stress to 0.9×yield per B31.3 §345.5.4:contentReference[oaicite:1]{index=1}
  const sTestAllowable = yieldStrength != null ? (5 / 6) * yieldStrength : S;
  state.minWallTest = (Pt * R0) / (sTestAllowable + 0.5 * Pt);

  // B31.3 design thickness for a straight cylinder: t = (P·D)/(2·(S – 0.6P))
  if (S > 0.6 * P) {
    state.minWallB313 = (P * outsideDiameter) / (2 * (S - 0.6 * P));
  } else {
    state.minWallB313 = null;
  }
}

// Pipeline-code basis (B31.8 / B31.4): SMYS × design factor on nominal
// wall. Yield strength doubles as SMYS; the joint and temperature factors
// come from the selections.
function computePipeline(state) {
  if (!isPipelineCode(state.designCode)) {
    state.pipeline = null;
    state.temperatureFactor = 1;
    return;
  }
  const T =
    state.designCode === "B31.8"
      ? temperatureDerating(state.designTemperature)
      : 1;
  state.temperatureFactor = T;
  state.pipeline =
    T == null
      ? null
      : pipelineWall({
          code: state.designCode,
          outsideDiameter: state.outsideDiameter,
          wallThickness: state.wallThickness,
          smys: state.yieldStrength,
          designPressure: state.designPressure,
          designFactor: state.designFactor,
          jointFactor: jointFactorValue(state.jointFactorId),
          temperatureFactor: T,
          corrosionAllowance: state.corrosionAllowance,
          locationClass: state.locationClass,
        });
}

function computeThicknesses(state) {
  computeAllowableThicknesses(state);
  computePipeline(state);
}

const initialState = {
  // Design basis: the allowable-stress method the calculator started with,
  // or a pipeline code with its location-class design factor.
  designCode: "B31.3",
  b318Construction: "pipeline",
  locationClass: "2",
  designFactor: 0.6,
  jointFactorId: "seamless",
  designTemperature: 100,
  temperatureFactor: 1,
  corrosionAllowance: 0,
  pipeline: null,
  selectedNps: "",
  selectedSchedule: "",
  outsideDiameter: null,
  insideDiameter: null,
  wallThickness: null,
  selectedMaterial: "",
  yieldStrength: null,
  tensileStrength: null,
  allowableStress: null,
  designPressure: null,
  testPressure: null,
  minWallWorking: null,
  minWallTest: null,
  minWallB313: null,
};

const calculatorSlice = createSlice({
  name: "calculator",
  initialState,
  reducers: {
    resetState(state) {
      Object.assign(state, initialState);
    },
    updateNps(state, action) {
      const { nps, outsideDiameter } = action.payload;
      state.selectedNps = nps;
      state.outsideDiameter =
        outsideDiameter != null ? outsideDiameter : state.outsideDiameter;
      state.selectedSchedule = "";
      state.wallThickness = null;
      state.insideDiameter = null;
      computeThicknesses(state);
    },
    updateSchedule(state, action) {
      const { schedule, wallThickness } = action.payload;
      state.selectedSchedule = schedule;
      if (wallThickness != null) {
        state.wallThickness = wallThickness;
        if (state.outsideDiameter != null) {
          state.insideDiameter = state.outsideDiameter - 2 * wallThickness;
        }
      }
      computeThicknesses(state);
    },
    updateOutsideDiameter(state, action) {
      const value = parseFloat(action.payload);
      state.outsideDiameter = isNaN(value) ? null : value;
      if (state.insideDiameter != null && state.outsideDiameter != null) {
        state.wallThickness =
          (state.outsideDiameter - state.insideDiameter) / 2;
      }
      computeThicknesses(state);
    },
    updateInsideDiameter(state, action) {
      const value = parseFloat(action.payload);
      state.insideDiameter = isNaN(value) ? null : value;
      if (state.outsideDiameter != null && state.insideDiameter != null) {
        state.wallThickness =
          (state.outsideDiameter - state.insideDiameter) / 2;
      }
      computeThicknesses(state);
    },
    updateWallThickness(state, action) {
      const value = parseFloat(action.payload);
      state.wallThickness = isNaN(value) ? null : value;
      if (state.outsideDiameter != null && state.wallThickness != null) {
        state.insideDiameter = state.outsideDiameter - 2 * state.wallThickness;
      }
      computeThicknesses(state);
    },
    updateMaterial(state, action) {
      const { materialName, yieldStrength, tensileStrength, allowableStress } =
        action.payload;
      state.selectedMaterial = materialName;
      state.yieldStrength =
        yieldStrength != null ? yieldStrength : state.yieldStrength;
      state.tensileStrength =
        tensileStrength != null ? tensileStrength : state.tensileStrength;
      if (allowableStress != null) {
        state.allowableStress = allowableStress;
      } else if (state.yieldStrength != null && state.tensileStrength != null) {
        state.allowableStress = computeAllowable(
          state.yieldStrength,
          state.tensileStrength
        );
      }
      computeThicknesses(state);
    },
    updateYieldStrength(state, action) {
      const value = parseFloat(action.payload);
      state.yieldStrength = isNaN(value) ? null : value;
      if (
        !state.allowableStress &&
        state.yieldStrength != null &&
        state.tensileStrength != null
      ) {
        state.allowableStress = computeAllowable(
          state.yieldStrength,
          state.tensileStrength
        );
      }
      computeThicknesses(state);
    },
    updateTensileStrength(state, action) {
      const value = parseFloat(action.payload);
      state.tensileStrength = isNaN(value) ? null : value;
      if (
        !state.allowableStress &&
        state.yieldStrength != null &&
        state.tensileStrength != null
      ) {
        state.allowableStress = computeAllowable(
          state.yieldStrength,
          state.tensileStrength
        );
      }
      computeThicknesses(state);
    },
    updateAllowableStress(state, action) {
      const value = parseFloat(action.payload);
      state.allowableStress = isNaN(value) ? null : value;
      computeThicknesses(state);
    },
    updateDesignPressure(state, action) {
      const value = parseFloat(action.payload);
      state.designPressure = isNaN(value) ? null : value;
      computeThicknesses(state);
    },
    updateTestPressure(state, action) {
      const value = parseFloat(action.payload);
      state.testPressure = isNaN(value) ? null : value;
      computeThicknesses(state);
    },
    updateDesignCode(state, action) {
      state.designCode = action.payload;
      if (state.designCode === "B31.4") {
        state.designFactor = B314_DESIGN_FACTOR;
      } else if (state.designCode === "B31.8") {
        state.designFactor = b318DesignFactor(
          state.b318Construction,
          state.locationClass
        );
      }
      computeThicknesses(state);
    },
    updateB318Construction(state, action) {
      state.b318Construction = action.payload;
      state.designFactor = b318DesignFactor(
        state.b318Construction,
        state.locationClass
      );
      computeThicknesses(state);
    },
    updateLocationClass(state, action) {
      state.locationClass = action.payload;
      state.designFactor = b318DesignFactor(
        state.b318Construction,
        state.locationClass
      );
      computeThicknesses(state);
    },
    // Hand-set F for the constructions the tables cover that the selector
    // doesn't (crossings, bridges, near people).
    updateDesignFactor(state, action) {
      const value = parseFloat(action.payload);
      state.designFactor = isNaN(value) ? null : value;
      computeThicknesses(state);
    },
    updateJointFactor(state, action) {
      state.jointFactorId = action.payload;
      computeThicknesses(state);
    },
    updateDesignTemperature(state, action) {
      const value = parseFloat(action.payload);
      state.designTemperature = isNaN(value) ? null : value;
      computeThicknesses(state);
    },
    updateCorrosionAllowance(state, action) {
      const value = parseFloat(action.payload);
      state.corrosionAllowance = isNaN(value) ? 0 : value;
      computeThicknesses(state);
    },
  },
});

export const {
  resetState,
  updateNps,
  updateSchedule,
  updateOutsideDiameter,
  updateInsideDiameter,
  updateWallThickness,
  updateMaterial,
  updateYieldStrength,
  updateTensileStrength,
  updateAllowableStress,
  updateDesignPressure,
  updateTestPressure,
  updateDesignCode,
  updateB318Construction,
  updateLocationClass,
  updateDesignFactor,
  updateJointFactor,
  updateDesignTemperature,
  updateCorrosionAllowance,
} = calculatorSlice.actions;

export default calculatorSlice.reducer;
